// lib/appwriteFaq.js - FAQ service with dynamic company ID

// Global variable to store the current company ID
let currentCompanyId = null;

/**
 * Set the company ID (called from the main widget initialization)
 * @param {string} companyId - The company ID to use for API calls
 */
export const setCompanyId = (companyId) => {
  currentCompanyId = companyId;
  console.log('FAQ Service: Company ID set to:', companyId);
};

/**
 * Get the current company ID
 * @returns {string|null} - The current company ID or null if not set
 */
export const getCompanyId = () => {
  if (!currentCompanyId) {
    console.warn('FAQ Service: No company ID set. Make sure to call setCompanyId() first.');
  }
  return currentCompanyId;
};

/**
 * Fetch all FAQs for the company through the proxy API
 * @param {string} companyId - Optional company ID override
 * @returns {Promise<Array>} - Array of FAQ documents
 */
export const fetchFAQs = async (companyId = null) => {
  // Use provided companyId or fall back to the global one
  const targetCompanyId = companyId || getCompanyId();
  
  if (!targetCompanyId) {
    console.error('FAQ Service: Cannot fetch FAQs - no company ID available');
    return [];
  }
  
  try {
    // Use the proxy API URL - this will be your Appwrite Function URL
    const proxyApiUrl = `https://680174d70eaff75f2a32.fra.appwrite.run/faqs/${targetCompanyId}`;
    
    console.log('Fetching FAQs from proxy API for company ID:', targetCompanyId);
    
    // Fetch from the proxy API
    const response = await fetch(proxyApiUrl);
    
    if (!response.ok) {
      throw new Error(`API returned status ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success) {
      console.log(`Successfully fetched ${data.faqs.length} FAQs for company ${targetCompanyId}`);
      return data.faqs;
    } else {
      console.warn('API returned error:', data.message);
      return [];
    }
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    return [];
  }
};

/**
 * Fetch a single FAQ by its document ID
 * @param {string} faqId - The document ID of the FAQ
 * @param {string} companyId - Optional company ID override
 * @returns {Promise<object|null>} - The FAQ object or null if not found
 */
export const fetchFAQById = async (faqId, companyId = null) => {
  try {
    console.log('📋 Fetching FAQ by ID:', faqId);
    
    if (!faqId) {
      throw new Error('FAQ ID is required');
    }
    
    // First, fetch all FAQs for the company
    const allFAQs = await fetchFAQs(companyId);
    
    // Find the FAQ with the matching ID
    const faq = allFAQs.find(f => f.$id === faqId);
    
    if (faq) {
      console.log('✅ FAQ found by ID:', faq);
      return faq;
    } else {
      console.warn('⚠️ FAQ not found with ID:', faqId);
      return null;
    }
    
  } catch (error) {
    console.error('❌ Error fetching FAQ by ID:', error);
    return null;
  }
};

/**
 * Get FAQ categories from the fetched FAQs
 * @param {string} companyId - Optional company ID override
 * @returns {Promise<Array>} - Array of category names
 */
export const getFAQCategories = async (companyId = null) => {
  try {
    // Get categories from FAQs
    const faqs = await fetchFAQs(companyId);
    const categories = [...new Set(faqs.map(faq => faq.category).filter(Boolean))];
    
    console.log(`Found ${categories.length} FAQ categories:`, categories);
    return categories;
  } catch (error) {
    console.error('Error getting FAQ categories:', error);
    return [];
  }
};

/**
 * Fetch FAQs by category using the cached FAQs
 * @param {string} category - The category to filter by
 * @param {string} companyId - Optional company ID override
 * @returns {Promise<Array>} - Array of FAQ documents for the category
 */
export const fetchFAQsByCategory = async (category, companyId = null) => {
  try {
    // Fetch all FAQs first
    const allFAQs = await fetchFAQs(companyId);
    
    // If category is null or "all", return all FAQs
    if (!category || category === 'all') {
      return allFAQs;
    }
    
    // Filter FAQs by category
    const filteredFAQs = allFAQs.filter(faq => faq.category === category);
    console.log(`Found ${filteredFAQs.length} FAQs in category "${category}"`);
    
    return filteredFAQs;
  } catch (error) {
    console.error(`Error fetching FAQs for category ${category}:`, error);
    return [];
  }
};

/**
 * Search FAQs by question or answer content
 * @param {string} searchTerm - The search term to look for
 * @param {string} companyId - Optional company ID override
 * @returns {Promise<Array>} - Array of matching FAQ documents
 */
export const searchFAQs = async (searchTerm, companyId = null) => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return [];
    }
    
    // Fetch all FAQs first
    const allFAQs = await fetchFAQs(companyId);
    
    // Search in question and answer fields (case insensitive)
    const searchLower = searchTerm.toLowerCase();
    const matchingFAQs = allFAQs.filter(faq => 
      (faq.question && faq.question.toLowerCase().includes(searchLower)) ||
      (faq.answer && faq.answer.toLowerCase().includes(searchLower)) ||
      (faq.category && faq.category.toLowerCase().includes(searchLower))
    );
    
    console.log(`Found ${matchingFAQs.length} FAQs matching "${searchTerm}"`);
    return matchingFAQs;
    
  } catch (error) {
    console.error(`Error searching FAQs for term "${searchTerm}":`, error);
    return [];
  }
};

/**
 * Get FAQ statistics for the current company
 * @param {string} companyId - Optional company ID override
 * @returns {Promise<Object>} - Object with FAQ statistics
 */
export const getFAQStats = async (companyId = null) => {
  try {
    const allFAQs = await fetchFAQs(companyId);
    const categories = await getFAQCategories(companyId);
    
    // Count by status
    const statusCounts = allFAQs.reduce((acc, faq) => {
      const status = faq.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    
    // Count by category
    const categoryCounts = allFAQs.reduce((acc, faq) => {
      const category = faq.category || 'uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
    
    const stats = {
      total: allFAQs.length,
      categories: categories.length,
      statusBreakdown: statusCounts,
      categoryBreakdown: categoryCounts,
      lastUpdated: allFAQs.length > 0 ? 
        Math.max(...allFAQs.map(faq => new Date(faq.$updatedAt || faq.$createdAt).getTime())) : null
    };
    
    console.log('FAQ Statistics:', stats);
    return stats;
    
  } catch (error) {
    console.error('Error getting FAQ statistics:', error);
    return {
      total: 0,
      categories: 0,
      statusBreakdown: {},
      categoryBreakdown: {},
      lastUpdated: null
    };
  }
};